import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Building2, Loader2, CheckCircle2, XCircle, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EnterpriseNumberField = ({ value, onChange, verified, onVerified, foreign, onForeignChange }) => {
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');

  const verify = async () => {
    setError('');
    setChecking(true);
    try {
      const res = await axios.get(`${API}/kbo/lookup`, { params: { number: value } });
      const d = res.data;
      if (!d.registrable) {
        setError(d.reason);
        onVerified(null);
      } else if (d.already_registered) {
        setError('Une entreprise avec ce numéro BCE est déjà inscrite sur BuildConnect.');
        onVerified(null);
      } else {
        onVerified(d);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Vérification impossible pour le moment.');
      onVerified(null);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="enterprise-number-field">
      <Label htmlFor="enterprise_number">Numéro d'entreprise (BCE)</Label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4B5563]" />
          <Input
            id="enterprise_number" name="enterprise_number" placeholder="BE0XXX.XXX.XXX"
            value={value} disabled={foreign}
            onChange={(e) => { onChange(e.target.value); onVerified(null); setError(''); }}
            className="pl-10 input-field font-mono" data-testid="register-enterprise-number"
          />
        </div>
        <Button type="button" variant="outline" onClick={verify} disabled={checking || foreign || !value} data-testid="verify-enterprise-btn">
          {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vérifier'}
        </Button>
      </div>

      {verified && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="enterprise-verified-card">
          <p className="flex items-center gap-1.5 font-semibold text-emerald-800"><CheckCircle2 className="w-4 h-4" /> {verified.name}</p>
          <p className="text-emerald-700 text-xs mt-1">{verified.address} · {verified.status} — {verified.juridical_situation}</p>
          {verified.nace_codes?.length > 0 && (
            <p className="text-emerald-700 text-xs mt-1">NACE-BEL : {verified.nace_codes.slice(0, 3).map((n) => n.code).join(', ')}</p>
          )}
          <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Données officielles BCE — le formulaire a été pré-rempli.</p>
        </div>
      )}

      {error && (
        <p className="flex items-start gap-1.5 text-sm text-red-600" data-testid="enterprise-error"><XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}</p>
      )}

      <label className="flex items-center gap-2 text-xs text-[#4B5563] cursor-pointer">
        <Checkbox checked={foreign} onCheckedChange={(v) => { onForeignChange(!!v); onVerified(null); setError(''); }} data-testid="foreign-company-checkbox" />
        Mon entreprise n'est pas belge (Luxembourg / France)
      </label>
    </div>
  );
};
